import React, { useState } from "react";
import styles from "./ProjectGroupList.module.scss";
import { FormGroup, FormInstance, Project, Source, SyncConfig, User } from "db/definitions";
import _getProject from "utils/_getProject";
import { Button, ProgressMetadata, Typography } from "@factorearth/component-library";
import { useTheme } from "hooks/useSingleton";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useNavigate } from "react-router";
import { mergeAndSave, prepDbForMerge } from "db/merge";
import { db } from "db/db";
import { updateRecentProjects } from "utils/localstorage/projects";
import { useDownloadImagesModal } from "hooks/useDownloadImageModal";
interface ProjectGroupList {
	groupedProject: Project[];
	group: string;
	numOfGroups: number;
	key: string;
	online: boolean;
	setProgress: (progress: Partial<ProgressMetadata>) => void;
	user: User | null | undefined;
	addFormGroupsInState: (formGroups: FormGroup[]) => void;
	updateProjectsSyncMenu: (project: Project) => void;
}

const ProjectGroupList = (props: ProjectGroupList) => {
	const { groupedProject, group, numOfGroups, key, online, user, updateProjectsSyncMenu, addFormGroupsInState, setProgress } = props;
	const color = useTheme();
	const navigate = useNavigate();

	const [groupToggle, setGroupToggle] = useState<boolean>(true);

	const [DownloadImagesModal, showDownloadImagesModal] = useDownloadImagesModal();

	const clickIntoProject = (project: Project) => {
		if (!project.offline) navigate(`/project/${project.id}`);
		updateRecentProjects(project);
	};

	async function mergeLocalToRemote(syncConfig: SyncConfig, offlineProject: Project, formGroupIds: string[]) {
		if (offlineProject && user) {
			const oppositeMap: Record<Source, Source> = {
				local: "remote",
				remote: "local",
				json: "json",
			};
			const forceUpdateData = `REFRESH_${Date.now()}`;
			const sourceProjCopy: Project | undefined = { ...offlineProject, forceUpdateData };
			const destinationProjCopy: Project | undefined = { ...offlineProject, forceUpdateData };

			if (sourceProjCopy) {
				setProgress({
					value: 20,
					variant: "info",
					message: `Preparing ${syncConfig.source} database for merge`,
					title: "Syncing FormGroups",
					canClose: false,
					show: true,
				});
				const donorData = await prepDbForMerge(user, syncConfig, formGroupIds, sourceProjCopy);
				if (donorData.imagesCouldBeDownloaded) {
					syncConfig.savePhotos = !!(await showDownloadImagesModal());
				}
				setProgress({
					value: 40,
					variant: "info",
					message: `Preparing ${syncConfig.destination} database for merge`,
					title: "Syncing FormGroups",
					canClose: false,
					show: true,
				});
				const destinationSyncConfig: SyncConfig = {
					online,
					source: oppositeMap[syncConfig.source],
					destination: oppositeMap[syncConfig.destination],
				};
				const receiverData = await prepDbForMerge(user, destinationSyncConfig, formGroupIds, destinationProjCopy);
				setProgress({
					value: 60,
					variant: "info",
					message: `Merging ${syncConfig.source} data to ${syncConfig.destination} database`,
					title: "Syncing FormGroups",
					canClose: false,
					show: true,
				});
				const mergeDataToSave = await mergeAndSave(donorData, receiverData, syncConfig, { username: user?.username });

				return {
					mergeDataToSave,
					donorData,
					receiverData,
				};
			}
		}
	}
	const syncProjectOnline = async (project: Project) => {
		const formGroups = await db.formGroup.where("projectId").equals(project.id).toArray();
		const syncConfig: SyncConfig = {
			online,
			source: "local",
			destination: "remote",
		};
		setProgress({
			value: 0,
			variant: "info",
			message: "Preparing to merge...",
			title: "Syncing Offline Project to Online",
			canClose: false,
			show: true,
		});
		const result = await mergeLocalToRemote(
			syncConfig,
			project,
			formGroups.map((formGroup: FormGroup) => formGroup.id),
		);
		if (result) {
			const { mergeDataToSave, donorData } = result;
			setProgress({
				value: 80,
				variant: "info",
				message: "Updating State...",
				title: "Syncing Offline Project to Online",
				canClose: false,
				show: true,
			});
			const onlineCopy = [...formGroups];
			mergeDataToSave.formGroups.forEach((formGroup) => {
				const fgCopy: FormGroup = { ...formGroup };
				const formInstances = [];
				for (const formId of fgCopy.formOrder) {
					const formInstance = donorData.formInstances.get(formId);
					if (formInstance) {
						const formCopy: FormInstance = { ...formInstance, fieldInstances: { items: [] } };
						const formTemplate = donorData.formTemplates.get(formCopy.formTemplateId);
						if (formTemplate) {
							const formTemplateCopy = { ...formTemplate };
							formCopy.formTemplate = formTemplateCopy;
						}
						formInstances.push(formCopy);
					}
				}
				fgCopy.formInstances = { items: formInstances };
				fgCopy.syncedAt = new Date().toISOString();

				const foundIndex = onlineCopy.findIndex((fg) => fg.id === fgCopy.id);
				if (foundIndex !== -1) {
					onlineCopy[foundIndex] = fgCopy;
				} else {
					onlineCopy.push(fgCopy);
				}
			});
			addFormGroupsInState(onlineCopy);
			const projectCopy = { ...mergeDataToSave.projects[0], formGroups: { items: onlineCopy } };
			updateProjectsSyncMenu(projectCopy);

			setProgress({
				value: 100,
				variant: "success",
				message: "Offline Project Successfully Synced Online",
				title: "Offline Project Successfully Synced Online",
				canClose: true,
				show: true,
				timeout: 3500,
			});
		}
	};

	return (
		<div key={key} className={styles.projectGroupListWrapper}>
			<DownloadImagesModal color={color} />
			<div className={styles.groupName} style={{ background: color.background.tertiary, borderBottom: `0.5px solid ${color.background.secondary}` }}>
				<Typography variant={"Heading 3"} textColor={color.text.primary}>
					{group}
				</Typography>
				{numOfGroups > 1 && (
					<div
						className={styles.groupToggleDiv}
						onClick={() => {
							setGroupToggle(!groupToggle);
						}}>
						{groupToggle ? <FiChevronUp /> : <FiChevronDown />}
					</div>
				)}
			</div>
			{groupToggle &&
				groupedProject.map((project) => {
					return (
						<div
							className={styles.projectNameWrapper}
							style={{
								background: color.background.primary,
								borderBottom: `0.5px solid ${color.background.secondary}`,
								cursor: project.offline ? "not-allowed" : "pointer",
							}}
							onClick={() => {
								clickIntoProject(project);
							}}>
							<Typography variant={"Heading 5"} textColor={project.offline ? color.text.secondary : color.text.primary}>
								{project.name === "" ? "(no project name)" : project.name}
							</Typography>
							{project.offline && online && (
								<Button colorPalette={color} variant="outlined" label="Sync" onClick={() => syncProjectOnline(project)} />
							)}
							{/* Leaving this becasue styling it was a pain and we will need it in the future
                        <div className={styles.editIcon}>
                            <FiEdit />
                        </div> */}
						</div>
					);
				})}
		</div>
	);
};

export default ProjectGroupList;
